import {createRouter, createWebHistory, type RouteRecordRaw,} from "vue-router"
import {RouterNames} from "@/router/routerNames.ts";
import {useAuthStore} from "@/store/auth-store.ts";
import {Roles} from "@/utils/constants/common.constants.ts";
import CommonUtils from "@/utils/CommonUtils.ts";

export const routes: RouteRecordRaw[] = [
  // ===== Public =====
  {
    name: RouterNames.HOME,
    path: "/",
    component: () => import("@/views/HomePage.vue"),
  },
  // ===== Auth =====
  {
    name: RouterNames.AUTH_LOGIN,
    path: "/auth/login",
    component: () => import("@/views/auth/LoginPage.vue"),
  },
  {
    name: RouterNames.AUTH_COMPLETE_PROFILE,
    path: "/auth/complete-profile", // todo::
    component: () => import("@/views/auth/CompleteProfilePage.vue"),
    meta: {requiresAuth: true},
  },
  // ===== Student Area =====
  {
    name: RouterNames.STUDENT_GRADES,
    path: "/dashboard/my-grades",
    component: () => import("@/views/dashboard/student/StudentGradesPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.STUDENT_PAYMENTS,
    path: "/dashboard/student-payments",
    component: () => import("@/views/dashboard/student/StudentPaymentsPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.STUDENT_EXAMS,
    path: "/dashboard/student-exams",
    component: () => import("@/views/dashboard/student/StudentExamsPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.STUDENT_EXAM,
    path: "/dashboard/student-exams/:examId",
    component: () => import("@/views/dashboard/student/StudentExamPage.vue"),
    meta: {requiresAuth: true},
    props: true
  },
  {
    name: RouterNames.STUDENT_ATTENDANCE,
    path: "/dashboard/attendance",
    component: () => import("@/views/dashboard/student/StudentAttendancePage.vue"),
    meta: {requiresAuth: true},
  },
  // ===== Admin Area =====
  {
    name: RouterNames.DASHBOARD_HOME,
    path: "/dashboard",
    component: () => import("@/views/dashboard/DashboardWrapperPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.ADMIN_VIEW_USERS,
    path: "/dashboard/view-users",
    component: () => import("@/views/dashboard/admin/ViewUsersPage.vue"),
    meta: {requiresAuth: true, keepAlive: true},
  },
  {
    name: RouterNames.ADMIN_GRADES,
    path: "/dashboard/grades",
    component: () => import("@/views/dashboard/admin/GradesPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.ADMIN_EXAMS,
    path: "/dashboard/exams",
    component: () => import("@/views/dashboard/admin/ExamsPage.vue"),
    meta: {requiresAuth: true, minRoleLevel: CommonUtils.toRoleLevel(Roles.SubAdmin)},
  },
  {
    name: RouterNames.ADMIN_LESSONS,
    path: "/dashboard/lessons",
    component: () => import("@/views/dashboard/admin/LessonsPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.ADMIN_PAYMENTS,
    path: "/dashboard/payments",
    component: () => import("@/views/dashboard/admin/PaymentsPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.ADMIN_GROUPS,
    path: "/dashboard/groups",
    component: () => import("@/views/dashboard/admin/GroupsPage.vue"),
    meta: {requiresAuth: true},
  },
  {
    name: RouterNames.ADMIN_LESSON_ATTENDANCE,
    path: "/dashboard/lessons/:lessonId",
    component: () => import("@/views/dashboard/admin/LessonPage.vue"),
    meta: {requiresAuth: true},
    props: true
  },
  {
    name: RouterNames.PROFILE,
    path: "/profile",
    component: () => import("@/views/ProfilePage.vue"),
    meta: {requiresAuth: true},
  },
  // ===== Error / Fallback =====
  {
    name: RouterNames.LINK_EXPIRED,
    path: "/expired-link",
    component: () => import("@/views/errors/LinkExpired.vue"),
  },
  {
    name: RouterNames.NOT_FOUND,
    path: "/:pathMatch(.*)*",
    component: () => import("@/views/errors/NotFoundPage.vue"),
  }
]
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(async (to, __, next) => {
  const auth = useAuthStore()

  if (!auth.isAuthReady)
    await auth.initUser()

  if (auth.isLoggedIn && !auth.user?.isProfileCompleted && to.name !== RouterNames.AUTH_COMPLETE_PROFILE)
    return next({name: RouterNames.AUTH_COMPLETE_PROFILE});

  if (auth.isLoggedIn && to.name === RouterNames.HOME)
    return next({name: RouterNames.DASHBOARD_HOME})

  if (to.meta.requiresAuth && !auth.isLoggedIn)
    return next({name: RouterNames.AUTH_LOGIN})

  next()
})

export default router;
